import { CustomException } from '@tencent/ppd-base-libs';
import { DpMessageCommonRes, QryDpMsgReq } from '../types/index';
import { getUin, isValidUinFormat } from '../utils/uin';
import { queryDpMessageByDpId } from './query-dp-message-by-dpid';
import { queryDpMessageByPosId } from './query-dp-message-by-posid';

// 用于自测投放计划的字段
type DpField = 'dpId' | 'pageId' | 'positionId';
// queryDpMessage 查询入参
type QryParams = Omit<QryDpMsgReq, 'appid' | 'dpChannel' | 'uin'>;

/**
 * feature:
 * 供外部使用，同时查两种模式；
 * 自测模式：根据urlParams查询自测投放计划(dpId)；
 * 线上模式：根据函数入参查询已上线且符合规则的投放计划；
*/
export const queryDpMessage = async (qryParams: QryParams) => {
  // 自动获取uin - 如果uin为空或者不符合钱包uin格式，那么new CustomException
  const uin = getUin();
  if (!isValidUinFormat(uin)) {
    new CustomException({ uin }, 'dpLoader-UinError', 'uin为空或不符合钱包uin格式');
    return;
  }
  const url = window.location.href;
  const [dpId, pageId, positionId] = ['dpId', 'pageId', 'positionId'].map(item => getDpFieldValue(item as DpField, url));
  // 检查是否需要查自测计划
  const shouldQuerySelfCheckDpPlan = shouldQueryDpMessageByDpId({ dpId, pageId, positionId }, qryParams);

  const qryParamsWithUin = { uin, ...qryParams };
  // 待查询的promise
  const qryPromises: Array<Promise<DpMessageCommonRes | undefined>> = [queryDpMessageByPosId(qryParamsWithUin)];
  if (shouldQuerySelfCheckDpPlan) {
    // 如果需要查自测计划，则拼装请求
    qryPromises.push(queryDpMessageByDpId({ ...qryParamsWithUin, dpId: Number(dpId) }));
  }
  const [commonDpMsg, selfCheckDpMsg] = await Promise.all(qryPromises);
  if (commonDpMsg && selfCheckDpMsg?.dpList && selfCheckDpMsg?.dpList.length > 0) {
    // 如果自测计划有返回内容，则插入到投放计划list第一个
    // selfCheckDpMsg只会有一个dpItem
    return { ...commonDpMsg, dpList: [selfCheckDpMsg.dpList[0], ...commonDpMsg.dpList] };
  }
  return commonDpMsg;
};

/**
 * 检查是否需要查询自测计划（根据dpId查投放计划）
 * @param urlParams - { dpId, pageId, positionId } 来自url参数的字段
 * @param qryParams queryDpMessageByPosIdByPosOrDpId查询入参
 */
export const shouldQueryDpMessageByDpId = (
  { dpId, pageId, positionId }: Record<DpField, string>,
  qryParams: QryParams,
) => {
  // 三个url字段都不为空
  if (!dpId || !pageId || !positionId) {
    return false;
  }
  // url字段pageId和positionId在qryParams中
  const { dpPage: paramsPageId, dpPositions: paramsPositionIds } = qryParams;
  const isPageIdValid = paramsPageId === pageId;
  const isPositionIdValid = paramsPositionIds?.includes(positionId);
  return isPageIdValid && isPositionIdValid;
};

/**
 * 从url中提取投放计划相关字段
 * 如：dpId=60180836&pageId=110004490719&positionId=1100044907190001
 */
export const getDpFieldValue = (field: DpField, url: string) => {
  const regStr = `${field}=(\\d+)`;
  const reg = new RegExp(regStr);
  const regResult = reg.exec(url);
  const resultStr = regResult?.[1] || '';
  return resultStr;
};
